/**
 * React Query hook for pending tenant requests
 *
 * Provides data fetching for non-fulfilled tenant requests.
 */

import { useQuery } from '@tanstack/react-query';
import { TenantRequestApiService } from '../services/tenantRequestApi';
import type { TenantRequest } from '../types/tenantRequest';

/**
 * Query key factory for pending requests
 */
export const pendingRequestsKeys = {
  all: ['pendingRequests'] as const,
  list: (organizationId?: number) => ['pendingRequests', 'list', organizationId] as const,
};

/**
 * Hook return type for usePendingRequests
 */
export interface UsePendingRequestsResult {
  pendingRequests: TenantRequest[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * React Query hook for fetching pending tenant requests
 *
 * Fetches tenant requests and filters to only return non-fulfilled ones
 * (pending, in_process, rejected). Can optionally filter by organization.
 *
 * @param organizationId - Optional organization ID to filter requests by
 * @returns Pending requests data, loading states, and refetch function
 *
 * @example
 * ```tsx
 * // Fetch all pending requests (global admin view)
 * const { pendingRequests, isLoading, refetch } = usePendingRequests();
 *
 * // Fetch pending requests for a specific organization
 * const { pendingRequests, isLoading, refetch } = usePendingRequests(orgId);
 * ```
 */
export function usePendingRequests(organizationId?: number): UsePendingRequestsResult {
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: pendingRequestsKeys.list(organizationId),
    queryFn: async () => {
      try {
        const response = await TenantRequestApiService.listRequests({
          organization_id: organizationId,
          page: 1,
          page_size: 50,
        });
        // Filter to only show non-fulfilled requests
        return response.items.filter((request) => request.status !== 'fulfilled');
      } catch {
        // Silently return empty array on error (not critical data)
        return [];
      }
    },
  });

  const refetch = async () => {
    await queryRefetch();
  };

  return {
    pendingRequests: data ?? [],
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
  };
}
